// src/components/admin/dsat/DSATManagement.js
import React, { useState, useEffect } from 'react';
import { db, storage } from '../../../firebaseConfig';
import { collection, doc, writeBatch, onSnapshot, updateDoc, query, orderBy } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import Papa from 'papaparse';
import { backdropDismiss } from '../../../utils/backdropDismiss';

const styles = {
  container: { padding: '24px', maxWidth: '1400px', margin: '0 auto', height: 'calc(100vh - 100px)' },
  grid: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px', height: '100%', alignItems: 'start' },
  
  // Sidebar
  sidebar: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  sidebarHeader: { padding: '16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontWeight: 700, color: '#111827', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  testList: { flex: 1, overflowY: 'auto' },
  testItem: (active) => ({
    padding: '14px 16px', cursor: 'pointer',
    background: active ? '#eff6ff' : '#fff', color: active ? '#2563eb' : '#4b5563',
    borderLeft: active ? '4px solid #2563eb' : '4px solid transparent',
    borderBottom: '1px solid #f3f4f6', fontSize: '14px', fontWeight: active ? 600 : 400
  }),
  newBtn: { fontSize: '12px', padding: '4px 8px', background: '#2563eb', color: '#fff', borderRadius: '4px', border: 'none', cursor: 'pointer' },

  // Content
  content: { height: '100%', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'hidden' },
  
  // Uploader Card
  card: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', flexShrink: 0 },
  title: { fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#6b7280', marginBottom: '16px' },
  formRow: { display: 'flex', gap: '12px', alignItems: 'flex-end' },
  input: { flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' },
  uploadBtn: { padding: '9px 20px', background: '#111827', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' },

  // Question List (Scrollable)
  qListContainer: { flex: 1, background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  qHeader: { padding: '12px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 700, color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backBtn: { fontSize: '12px', padding: '6px 12px', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', cursor: 'pointer', color: '#374151' },
  
  qScroll: { flex: 1, overflowY: 'auto', padding: '0' },
  qRow: { display: 'flex', gap: '16px', padding: '16px', borderBottom: '1px solid #f3f4f6', alignItems: 'start' },
  
  // Image Styles
  qImageContainer: { width: '200px', height: '120px', border: '1px solid #eee', borderRadius: '4px', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'zoom-in' },
  qImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  
  qInfo: { flex: 1 },
  qMeta: { fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'flex', gap: '12px' },
  badge: { background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 },
  editBtn: { padding: '6px 12px', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 },

  // Modal (Edit)
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#fff', borderRadius: '12px', width: '500px', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },

  // Image Preview Modal (Zoom)
  imgPreviewOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, cursor: 'zoom-out' },
  imgPreviewContent: { maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px', boxShadow: '0 0 20px rgba(0,0,0,0.5)' }
};

// --- Helper Component to Fetch & Display Image ---
const QuestionImage = ({ path, onClick }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!path) return;
    let isMounted = true;
    
    getDownloadURL(ref(storage, path))
      .then((u) => { if(isMounted) setUrl(u); })
      .catch((err) => { 
        console.warn("Image load fail:", path, err); 
        if(isMounted) setError(true); 
      });

    return () => { isMounted = false; };
  }, [path]);

  if (error) return <div style={{fontSize:10, color:'#ef4444'}}>Image not found</div>;
  if (!url) return <div style={{fontSize:10, color:'#9ca3af'}}>Loading...</div>;
  return <img src={url} alt="Question" style={styles.qImg} onClick={() => onClick(url)} />;
};

export default function DSATManagement() {
  const [tests, setTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [viewMode, setViewMode] = useState('upload'); 

  // Upload State
  const [newTestId, setNewTestId] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [isUploading, setIsUploading] = useState(false);

  // Edit State
  const [editingQ, setEditingQ] = useState(null); 
  
  // Image Preview State
  const [previewImage, setPreviewImage] = useState(null);

  // 1. Load Tests
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "sat_diagnostic_tests"), (snapshot) => {
      setTests(snapshot.docs.map(d => d.id));
    });
    return () => unsub();
  }, []);

  // 2. Load Questions when Test Selected
  useEffect(() => {
    if (!selectedTestId) {
      setQuestions([]);
      return;
    }
    setViewMode('list');
    
    const qQuery = query(collection(db, "sat_diagnostic_tests", selectedTestId, "questions"), orderBy("module"), orderBy("questionNumber"));
    const unsub = onSnapshot(qQuery, (snap) => {
      setQuestions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [selectedTestId]);

  const getModuleFromName = (name) => {
    if (!name) return 'Unknown';
    const n = name.toLowerCase();
    if (n.includes('english_routing') || n.includes('rw_routing')) return 'RW_Routing';
    if (n.includes('english_module_a') || n.includes('rw_module_a')) return 'RW_Module_A';
    if (n.includes('english_module_b') || n.includes('rw_module_b')) return 'RW_Module_B';
    if (n.includes('math_routing')) return 'Math_Routing';
    if (n.includes('math_module_a')) return 'Math_Module_A';
    if (n.includes('math_module_b')) return 'Math_Module_B';
    return 'Unknown';
  };

  // --- ACTION: UPLOAD ---
  const handleUpload = async () => {
    if (!newTestId.trim() || !csvFile) {
      setStatus({ type: 'error', msg: 'Please enter a Test ID and select a CSV file.' });
      return;
    }
    setIsUploading(true);
    setStatus({ type: 'success', msg: 'Parsing...' });

    Papa.parse(csvFile, {
      header: true, skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;
        if (!rows.length) return;

        try {
          setStatus({ type: 'success', msg: `Uploading ${rows.length} questions...` });
          let batch = writeBatch(db);
          let count = 0, writes = 0;

          // Create Parent Doc
          batch.set(doc(db, "sat_diagnostic_tests", newTestId), { testId: newTestId, createdAt: new Date() }, { merge: true });
          writes++;

          for (const row of rows) {
            if (!row.Name || !row.Answer) continue;

            const rawName = String(row.Name).trim();
            const qNum = Number(row['Question Number'] || 0);
            
            // Fix unique ID
            let uniqueId = rawName;
            if (!uniqueId.match(/Q\d+$/i) && !uniqueId.includes(String(qNum))) {
                uniqueId = `${rawName}_Q${qNum}`;
            }

            const qRef = doc(db, "sat_diagnostic_tests", newTestId, "questions", uniqueId);
            
            const subKey = row.Subcategory ? String(row.Subcategory).trim().replace(/–/g, "-").replace(/[^\w]+/g, "_") : "";
            const imagePath = `sat_diagnostic/${newTestId}/${uniqueId}.png`;

            const data = {
              questionId: uniqueId,
              module: getModuleFromName(rawName),
              questionNumber: qNum,
              correctAnswer: String(row.Answer).trim(),
              difficulty: row.Difficulty || 'Medium',
              category: row.Category || 'Uncategorized',
              subcategory: row.Subcategory || '',
              subcategoryKey: subKey,
              imagePath,
              explanationLink: row['Explanation Link'] || ''
            };

            batch.set(qRef, data);
            count++; writes++;

            if (writes >= 450) { await batch.commit(); batch = writeBatch(db); writes = 0; }
          }
          if (writes > 0) await batch.commit();

          setStatus({ type: 'success', msg: `✅ Success! Created ${newTestId} with ${count} questions.` });
          setNewTestId(''); setCsvFile(null);
          setSelectedTestId(newTestId); 
          
        } catch (err) {
          console.error(err);
          setStatus({ type: 'error', msg: `Error: ${err.message}` });
        } finally { setIsUploading(false); }
      }
    });
  };

  // --- ACTION: EDIT ---
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingQ) return;
    try {
      const ref = doc(db, "sat_diagnostic_tests", selectedTestId, "questions", editingQ.id);
      await updateDoc(ref, {
        correctAnswer: editingQ.correctAnswer,
        difficulty: editingQ.difficulty,
        subcategory: editingQ.subcategory,
        explanationLink: editingQ.explanationLink
      });
      setEditingQ(null); 
    } catch (err) {
      alert("Error saving: " + err.message);
    }
  };

  // --- ACTION: GO BACK ---
  const handleBack = () => {
    setSelectedTestId(null);
    setViewMode('upload');
  };

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        
        {/* LEFT SIDEBAR */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <span>Tests</span>
            <button style={styles.newBtn} onClick={handleBack}>+ New</button>
          </div>
          <div style={styles.testList}>
            {tests.map(id => (
              <div 
                key={id} 
                style={styles.testItem(selectedTestId === id)}
                onClick={() => setSelectedTestId(id)}
              >
                {id}
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT CONTENT AREA */}
        <div style={styles.content}>
          
          {/* MODE 1: UPLOAD FORM */}
          {viewMode === 'upload' && (
            <div style={styles.card}>
              <h2 style={styles.title}>Create New Diagnostic Test</h2>
              <p style={styles.subtitle}>Upload CSV to generate questions. (Fixes duplicate names automatically)</p>
              
              <div style={styles.formRow}>
                <div style={{flex: 1}}>
                  <div style={{fontSize:12, marginBottom:4, fontWeight:600}}>Test ID (e.g. sat_diagnostic_test1)</div>
                  <input style={styles.input} value={newTestId} onChange={e => setNewTestId(e.target.value)} placeholder="Unique Test ID" />
                </div>
                <div style={{flex: 2}}>
                  <div style={{fontSize:12, marginBottom:4, fontWeight:600}}>CSV File</div>
                  <input type="file" accept=".csv" onChange={e => setCsvFile(e.target.files[0])} style={styles.input} />
                </div>
                <button style={styles.uploadBtn} onClick={handleUpload} disabled={isUploading}>
                  {isUploading ? 'Generating...' : 'Upload'}
                </button>
              </div>
              {status.msg && (
                <div style={{marginTop:15, fontSize:14, color: status.type === 'error' ? 'red' : 'green'}}>
                  {status.msg}
                </div>
              )}
            </div>
          )}

          {/* MODE 2: QUESTION LIST */}
          {viewMode === 'list' && selectedTestId && (
            <div style={styles.qListContainer}>
              <div style={styles.qHeader}>
                <span>Questions for: {selectedTestId} ({questions.length})</span>
                <button style={styles.backBtn} onClick={handleBack}>← Back to Dashboard</button>
              </div>
              <div style={styles.qScroll}>
                {questions.length === 0 && <div style={{padding:20, color:'#999'}}>No questions found. Try re-uploading.</div>}
                {questions.map(q => (
                  <div key={q.id} style={styles.qRow}>
                    {/* Display the Actual Image */}
                    <div style={styles.qImageContainer}>
                       <QuestionImage path={q.imagePath} onClick={setPreviewImage} />
                    </div>

                    <div style={styles.qInfo}>
                      <div style={{fontWeight:700, color:'#111827', marginBottom:4}}>{q.module} - #{q.questionNumber}</div>
                      <div style={styles.qMeta}>
                        <span style={styles.badge}>{q.category}</span>
                        <span style={styles.badge}>{q.difficulty}</span>
                        <span>Ans: <strong>{q.correctAnswer}</strong></span>
                      </div>
                      <div style={{fontSize:13, color:'#374151', marginTop:4}}>
                        {q.subcategory} 
                        {q.explanationLink && <a href={q.explanationLink} target="_blank" rel="noreferrer" style={{marginLeft:10, color:'#2563eb'}}>View Explanation</a>}
                      </div>
                      <div style={{fontSize: 10, color: '#9ca3af', marginTop: 4}}>{q.imagePath}</div>
                    </div>

                    <button style={styles.editBtn} onClick={() => setEditingQ(q)}>Edit</button>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* EDIT MODAL */}
      {editingQ && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3>Edit Question {editingQ.questionNumber}</h3>
            <form onSubmit={handleSaveEdit}>
              <div style={{marginBottom:12}}>
                <label style={{display:'block', fontSize:12, fontWeight:600}}>Correct Answer</label>
                <input 
                  style={styles.input} 
                  value={editingQ.correctAnswer} 
                  onChange={e => setEditingQ({...editingQ, correctAnswer: e.target.value})} 
                />
              </div>
              <div style={{marginBottom:12}}>
                <label style={{display:'block', fontSize:12, fontWeight:600}}>Difficulty</label>
                <select 
                  style={styles.input} 
                  value={editingQ.difficulty} 
                  onChange={e => setEditingQ({...editingQ, difficulty: e.target.value})} 
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
              <div style={{marginBottom:12}}>
                <label style={{display:'block', fontSize:12, fontWeight:600}}>Subcategory</label>
                <input 
                  style={styles.input} 
                  value={editingQ.subcategory} 
                  onChange={e => setEditingQ({...editingQ, subcategory: e.target.value})} 
                />
              </div>
              <div style={{marginBottom:12}}>
                <label style={{display:'block', fontSize:12, fontWeight:600}}>Explanation Link</label>
                <input 
                  style={styles.input} 
                  value={editingQ.explanationLink} 
                  onChange={e => setEditingQ({...editingQ, explanationLink: e.target.value})} 
                />
              </div>
              <div style={{marginBottom:12}}>
                <label style={{display:'block', fontSize:12, fontWeight:600}}>Image Path (Advanced)</label>
                <input 
                  style={{...styles.input, background: '#f3f4f6'}} 
                  value={editingQ.imagePath} 
                  disabled
                />
              </div>
              <div style={styles.modalActions}>
                <button type="button" onClick={() => setEditingQ(null)} style={{...styles.newBtn, background:'#fff', color:'#333', border:'1px solid #ccc'}}>Cancel</button>
                <button type="submit" style={{...styles.newBtn, padding:'8px 16px'}}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* IMAGE PREVIEW MODAL (Lightbox) */}
      {previewImage && (
        <div style={styles.imgPreviewOverlay} {...backdropDismiss(() => setPreviewImage(null))}>
          <img 
            src={previewImage} 
            alt="Zoomed Question" 
            style={styles.imgPreviewContent} 
            onClick={(e) => e.stopPropagation()} // Prevent closing if clicking image
          />
        </div>
      )}
    </div>
  );
}